import { useRef } from "react";
import { useFormContext } from "react-hook-form";
import useEvent from "react-use-event-hook";

import { BOOKING_BULK_FORM_FIELDS, type DirectionEnum } from "@ryde/shared";

import type {
  TFromWorkStationFieldName,
  TToWorkStationFieldName,
} from "../bulk-bookings.models";
import { getFromWorkFieldName, resolveFromWorkAutofill } from "../helpers";

/** Only a user's To Work pick fills From Work: the passenger prefill sets B from the passenger's own B stations. */
export const useFromWorkStationsAutofill = ({
  direction,
  onStationQuickAdded,
}: {
  direction: DirectionEnum;
  onStationQuickAdded: (stationId: number) => void;
}) => {
  const { getValues, setValue } = useFormContext();
  const stateContainer = { direction };
  const autofilledStationIdsRef = useRef<
    Partial<Record<TFromWorkStationFieldName, number>>
  >({});

  // Both To Work dropdowns write their form value before they report a pick, so the form is the one source.
  const autofillFromWork = useEvent(
    (toWorkFieldName: TToWorkStationFieldName): void => {
      const fromWorkFieldName = getFromWorkFieldName(toWorkFieldName);
      const autofill = resolveFromWorkAutofill({
        direction: stateContainer.direction,
        toWorkStationId: getValues(toWorkFieldName),
        fromWorkStationId: getValues(fromWorkFieldName),
        autofilledStationId: autofilledStationIdsRef.current[fromWorkFieldName],
      });

      if (!autofill) {
        return;
      }

      autofilledStationIdsRef.current[fromWorkFieldName] =
        autofill.stationId ?? undefined;
      setValue(fromWorkFieldName, autofill.stationId, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
  );

  const onHomeStationASelect = useEvent((): void => {
    autofillFromWork(BOOKING_BULK_FORM_FIELDS.originStationIdA);
  });

  const onHomeStationAQuickAdded = useEvent((stationId: number): void => {
    onStationQuickAdded(stationId);
    autofillFromWork(BOOKING_BULK_FORM_FIELDS.originStationIdA);
  });

  const onWorkStationASelect = useEvent((): void => {
    autofillFromWork(BOOKING_BULK_FORM_FIELDS.destinationStationIdA);
  });

  return {
    onHomeStationASelect,
    onHomeStationAQuickAdded,
    onWorkStationASelect,
  };
};
